import { useState, useEffect } from 'react';

import { Card, Col, Row } from 'react-bootstrap';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { useTranslation } from 'react-i18next';

import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';

import apiClient from 'helpers/apiService';
import Button from 'components/base/Button';
import { useNavigate } from 'react-router-dom';
import career from 'assets/img/th-each-ai/career.png';
import compass from 'assets/img/th-each-ai/compass.jpg';
import job_skills from 'assets/img/th-each-ai/job_skills.png';

import {
  faAngleDown,
  faPlus,
  faSquareEnvelope,
  faSquarePhone,

} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Deal } from 'data/crm/deals';
import { currencyFormat } from 'helpers/utils';
import FeatherIcon from 'feather-icons-react';
import { UilHeadphones, UilUser } from '@iconscout/react-unicons';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import Badge, { BadgeBg } from 'components/base/Badge';
import { faWhatsappSquare } from '@fortawesome/free-brands-svg-icons';
import { useDealsContext } from 'pages/template/providers/CrmDealsProvider';

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import { Calendar, ChevronLeft, ChevronRight, List, BarChart2, PieChart as PieChartIcon } from 'lucide-react';



interface SummaryTask {
  title: string;
  count: number;
  icon: any;
  trend: string;
  color: string;
}


import { faTasks, faSpinner, faCheckCircle, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

interface TimeData {
  data: string;
  completed: number;
  in_progress: number;
  to_start: number;
  archived: number;
}

type TaskStatus = keyof Omit<TimeData, 'data'>;

interface Task {
  id: number;
  title: string;
  status: TaskStatus;
  startDate: string | null;
  endDate: string | null;
  category: string;
  priority: string;
}

// Mock dei dati utente
const mockTasks: Task[] = [
  { id: 1, title: "Sviluppo homepage", status: "completed", startDate: "2025-04-01", endDate: "2025-04-03", category: "Frontend", priority: "Alta" },
  { id: 2, title: "Setup database", status: "completed", startDate: "2025-04-02", endDate: "2025-04-04", category: "Backend", priority: "Alta" },
  { id: 3, title: "Design UI componenti", status: "in_progress", startDate: "2025-04-03", endDate: null, category: "Design", priority: "Media" },
  { id: 4, title: "Implementazione API", status: "in_progress", startDate: "2025-04-04", endDate: null, category: "Backend", priority: "Alta" },
  { id: 5, title: "Testing funzionalità", status: "to_start", startDate: null, endDate: null, category: "QA", priority: "Media" },
  { id: 6, title: "Documentazione", status: "to_start", startDate: null, endDate: null, category: "Documentazione", priority: "Bassa" },
  { id: 7, title: "Review codice", status: "archived", startDate: "2025-03-20", endDate: "2025-03-25", category: "Sviluppo", priority: "Media" },
  { id: 8, title: "Ottimizzazione performance", status: "to_start", startDate: null, endDate: null, category: "Frontend", priority: "Bassa" },
];


const summaryTasks: SummaryTask[] = [
  {
    title: "Total Tasks",
    count: 24,
    icon: faTasks,
    trend: "+12% compared to last week",
    color: "primary"
  },
  {
    title: "In Progress",
    count: 8,
    icon: faSpinner,
    trend: "-5% compared to last week",
    color: "info"
  },
  {
    title: "Completed",
    count: 10,
    icon: faCheckCircle,
    trend: "+20% compared to last week",
    color: "success"
  },
  {
    title: "Late Tasks",
    count: 6,
    icon: faExclamationTriangle,
    trend: "+8% compared to last week",
    color: "warning"
  },
];

// Colori per i vari stati
const COLORS: Record<TaskStatus, BadgeBg> = {
  "completed": "success",
  "in_progress": "info",
  "to_start": "warning",
  "archived": "secondary"
};

const CHART_COLORS: Record<TaskStatus, string> = {
  "completed": '#004aad',
  "in_progress": '#00C49F',
  "to_start": '#f4af36',
  "archived": "green"
};



interface SummaryCardProps {
  task: SummaryTask;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ task }) => {
  return (
    <Card className="mb-3">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <p className="mb-0 fs-8 fw-semi-bold text-700 flex-1">{task.title}</p>
          <FontAwesomeIcon icon={task.icon} className={"text-700 fs-6 text-" + task.color} />
        </div>

        <div className="d-flex align-items-center mb-2">
          <p className="fs-5 fw-bold">{task.count}</p>
        </div>

        <div className="d-flex align-items-center mb-2">
          <p className="fs-8 fw-semi-bold">{task.trend}</p>
        </div>
      </Card.Body>
    </Card>
  );
};

const projectListTableColumns: ColumnDef<Task>[] = [
  {
    accessorKey: 'title',
    header: 'Activity',
    cell: ({ row: { original } }) => {
      const { title } = original;
      return (
        <Link to="#!" className="text-decoration-none fw-bold fs-8">
          {title}
        </Link>
      );
    },
    meta: {
      cellProps: { className: 'white-space-nowrap py-4' },
      headerProps: { style: { width: '30%' } }
    }
  },

  {
    header: 'Start date',
    accessorKey: 'startDate',
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-3' }
    }
  },
  {
    header: 'End date',
    accessorKey: 'endDate',
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-900 white-space-nowrap py-4' },
      headerProps: { style: { width: '15%' }, className: 'ps-3' }
    }
  },
  {
    accessorKey: 'task',
    header: 'Task',
    meta: {
      cellProps: { className: 'ps-3 text-900 py-4' },
      headerProps: { style: { width: '12%' }, className: 'ps-3' }
    }
  },

  {
    id: 'status',
    header: 'Status',
    //accessorFn: ({ status }) => status.label,
    cell: ({ row: { original } }) => {
      const { status } = original;
      return (
        <Badge variant="phoenix" bg={COLORS[status as TaskStatus]}>
          {status}
        </Badge>
      );
    },
    meta: {
      cellProps: { className: 'ps-8 py-4' },
      headerProps: { style: { width: '10%' }, className: 'ps-8' }
    }
  }
];

const ProjectListTable = () => {
  return (
    <div className="border-bottom">
      <AdvanceTable
        tableProps={{ className: 'phoenix-table border-top border-200 fs-9' }}
      />
      <AdvanceTableFooter pagination className="py-3" />
    </div>
  );
};


const TaskManagement = () => {

  const { t } = useTranslation();
  const navigate = useNavigate();

  const [tasks, setTasks] = useState(mockTasks);
  const [visualizzazione, setVisualizzazione] = useState('giornaliero');
  const [dataCorrente, setDataCorrente] = useState(new Date());
  const [tipoGrafico, setTipoGrafico] = useState('barre');

  //const [assessment, setAssessment] = useState<Assessment>();

  const breadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: t('Dashboard'),
      url: '/dashboard/home'
    },
    {
      label: t('Analytics.page_title'),
      url: '/analytics'
    },
    {
      label: t('StatisticsDashboard.basic_stats.tasks_completion'),
      active: true
    }
  ];


  const getGraficoData = () => {
    const oggi = new Date();

    // Conteggio task per stato
    const conteggioStati = {
      "completed": 0,
      "in_progress": 0,
      "to_start": 0,
      "archived": 0
    };

    tasks.forEach(task => {
      conteggioStati[task.status as TaskStatus]++;
    });

    // Prepara i dati per il grafico a barre/torta
    const pieData = Object.keys(conteggioStati).map(stato => ({
      nome: stato,
      valore: conteggioStati[stato as TaskStatus]
    }));


    // Prepara dati per il grafico a linee/barre temporale
    const timeData = [];

    if (visualizzazione === 'giornaliero') {
      // Ultimi 7 giorni
      for (let i = 6; i >= 0; i--) {
        const data = new Date();
        data.setDate(dataCorrente.getDate() - i);
        const dataParsed = `${data.getDate()}/${data.getMonth() + 1}`;

        const statiGiorno: TimeData = { data: dataParsed, completed: 0, in_progress: 0, to_start: 0, archived: 0 };

        tasks.forEach(task => {
          const dataInizio = task.startDate ? new Date(task.startDate) : null;
          const dataFine = task.endDate ? new Date(task.endDate) : null;

          if (dataInizio &&
            dataInizio.getDate() === data.getDate() &&
            dataInizio.getMonth() === data.getMonth() &&
            dataInizio.getFullYear() === data.getFullYear()) {
            statiGiorno[task.status as TaskStatus]++;
          }
        });

        timeData.push(statiGiorno);
      }
    } else if (visualizzazione === 'settimanale') {
      // Ultime 4 settimane
      for (let i = 3; i >= 0; i--) {
        const dataInizio = new Date(dataCorrente);
        dataInizio.setDate(dataCorrente.getDate() - (i * 7) - 6);
        const dataFine = new Date(dataCorrente);
        dataFine.setDate(dataCorrente.getDate() - (i * 7));

        const settimana = `S${3 - i + 1}`;
        const statiSettimana: TimeData = { data: settimana, completed: 0, in_progress: 0, to_start: 0, archived: 0 };

        tasks.forEach(task => {
          const dataTask = task.startDate ? new Date(task.startDate) : null;
          if (dataTask && dataTask >= dataInizio && dataTask <= dataFine) {
            statiSettimana[task.status as TaskStatus]++;
          }
        });

        timeData.push(statiSettimana);
      }
    } else if (visualizzazione === 'mensile') {
      // Ultimi 6 mesi
      const mesi = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

      for (let i = 5; i >= 0; i--) {
        const data = new Date(dataCorrente);
        data.setMonth(dataCorrente.getMonth() - i);
        const mese = mesi[data.getMonth()];
        const anno = data.getFullYear();

        const statiMese: TimeData = { data: `${mese} ${anno}`, completed: 0, in_progress: 0, to_start: 0, archived: 0 };

        tasks.forEach(task => {
          const dataTask = task.startDate ? new Date(task.startDate) : null;
          if (dataTask && dataTask.getMonth() === data.getMonth() && dataTask.getFullYear() === data.getFullYear()) {
            statiMese[task.status as TaskStatus]++;
          }
        });

        timeData.push(statiMese);
      }
    }

    return { pieData, timeData };
  };


  const { pieData, timeData } = getGraficoData();

  // Navigazione temporale
  const navigaIndietro = () => {
    const nuovaData = new Date(dataCorrente);
    if (visualizzazione === 'giornaliero') {
      nuovaData.setDate(dataCorrente.getDate() - 7);
    } else if (visualizzazione === 'settimanale') {
      nuovaData.setDate(dataCorrente.getDate() - 28);
    } else {
      nuovaData.setMonth(dataCorrente.getMonth() - 6);
    }
    setDataCorrente(nuovaData);
  };

  const navigaAvanti = () => {
    const nuovaData = new Date(dataCorrente);
    const oggi = new Date();

    if (visualizzazione === 'giornaliero') {
      nuovaData.setDate(dataCorrente.getDate() + 7);
    } else if (visualizzazione === 'settimanale') {
      nuovaData.setDate(dataCorrente.getDate() + 28);
    } else {
      nuovaData.setMonth(dataCorrente.getMonth() + 6);
    }

    if (nuovaData <= oggi) {
      setDataCorrente(nuovaData);
    } else {
      setDataCorrente(oggi);
    }
  };

  const table = useAdvanceTable({
    data: mockTasks,
    columns: projectListTableColumns,
    pageSize: 6,
    pagination: true,
    sortable: true
  });

  useEffect(() => {

  }, []);

  return (
    <>
      <PageBreadcrumb items={breadcrumbItems} />

      <div className="mt-4">
        <Row className="mt-3 mb-3">
          {summaryTasks.map((task, index) => (
            <Col sm={6} md={6} lg={6} key={index}>
              <SummaryCard task={task} />
            </Col>
          ))}
        </Row>



        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Dashboard di Monitoraggio Attività</h2>

          {/* Filtri e controlli */}
          <div className="flex flex-wrap gap-4 mb-6 items-center">
            <div className="flex gap-2 items-center">
              <span className="text-gray-700">Visualizzazione:</span>
              <select
                className="bg-gray-100 border border-gray-300 rounded px-3 py-1 text-gray-700"
                value={visualizzazione}
                onChange={(e) => setVisualizzazione(e.target.value)}
              >
                <option value="giornaliero">Giornaliero</option>
                <option value="settimanale">Settimanale</option>
                <option value="mensile">Mensile</option>
              </select>
            </div>
            {/* 
        <div className="flex gap-2 items-center">
          <span className="text-gray-700">Tipo grafico:</span>
          <div className="flex border border-gray-300 rounded overflow-hidden">
            <button 
              className={`p-2 ${tipoGrafico === 'barre' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`}
              onClick={() => setTipoGrafico('barre')}
            >
              <BarChart2 size={16} />
            </button>
            <button 
              className={`p-2 ${tipoGrafico === 'torta' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`}
              onClick={() => setTipoGrafico('torta')}
            >
              <PieChartIcon size={16} />
            </button>
            <button 
              className={`p-2 ${tipoGrafico === 'linee' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`}
              onClick={() => setTipoGrafico('linee')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 16L8 11L13 16L21 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
        */}

            {/* 
            <div className="flex gap-2 items-center ml-auto">
              <button
                className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700"
                onClick={navigaIndietro}
              >
                <ChevronLeft size={20} />
              </button>
              <div className="flex gap-1 items-center">
                <Calendar size={18} className="text-gray-500" />
                <span className="text-gray-700">{
                  visualizzazione === 'giornaliero'
                    ? `Ultima settimana`
                    : visualizzazione === 'settimanale'
                      ? `Ultimo mese`
                      : `Ultimi 6 mesi`
                }</span>
              </div>
              <button
                className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700"
                onClick={navigaAvanti}
              >
                <ChevronRight size={20} />
              </button>
            </div>
            */}
          </div>

          <Row>
            {/* Grafico principale */}
            <Col md={6} className="bg-gray-50 p-2 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-4 text-gray-700">Andamento Temporale</h3>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={350}>
                  {tipoGrafico === 'barre' ? (
                    <BarChart data={timeData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="data" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {Object.keys(COLORS).map((stato) => (
                        <Bar key={stato} dataKey={stato} name={stato.charAt(0).toUpperCase() + stato.slice(1)} fill={CHART_COLORS[stato as TaskStatus]} />
                      ))}
                    </BarChart>
                  ) : (
                    <LineChart data={timeData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="data" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      {Object.keys(COLORS).map((stato) => (
                        <Line
                          key={stato}
                          type="monotone"
                          dataKey={stato}
                          name={stato.charAt(0).toUpperCase() + stato.slice(1)}
                          stroke={CHART_COLORS[stato as TaskStatus]}
                          activeDot={{ r: 8 }}
                        />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>
            </Col>

            <Col md={6} className="bg-gray-50 p-2 rounded-lg shadow-sm">

              <div className="chart-container">
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="valore"
                      nameKey="nome"
                      label={({ nome, percent }) => `${nome}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[entry.nome as TaskStatus]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>

          {/* Lista task recenti */}
          <Row>
            <div className="bg-gray-50 mt-2 p-4 rounded-lg shadow-sm">

              <AdvanceTableProvider {...table}>
                <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center">
                  <h2 className="mb-0">
                    <span className="me-3">Le tue attività recenti</span>{' '}
                    <span className="fw-normal text-700">({mockTasks.length})</span>
                  </h2>
                </div>
                {/* <ProjectsTopSection activeView="list" /> */}
                <ProjectListTable />
              </AdvanceTableProvider>



            </div>
          </Row>
          {/* Indicatori riassuntivi */}

        </div>
      </div >






    </>
  );
};

export default TaskManagement;
